import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BoltIcon from '@mui/icons-material/Bolt';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Box from '@mui/material/Box';
import React from 'react';
import { Link } from 'react-router-dom';
import DeleteResourceDialog from './DeleteResourceDialog';
import PipelineActionDialog from './PipelineActionDialog';
import { getAccessToken } from '../utils/accessTokenStore';
import { isAdmin } from '../utils/utils';

type ResourceContextMenuProps = {
  request: any;
  onDeleteSuccess?: () => void;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
}

const ITEM_HEIGHT = 48;

function ResourceContextMenu(props: ResourceContextMenuProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [invalidateDialogOpen, setInvalidateDialogOpen] = React.useState(false);
  const [actionsMenuAnchor, setActionsMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [selectedAction, setSelectedAction] = React.useState<any>(null);
  const [pipelineActionDialogOpen, setPipelineActionDialogOpen] = React.useState(false);

  const open = Boolean(anchorEl);
  const actionsMenuOpen = Boolean(actionsMenuAnchor);
  const isAdminUser = React.useMemo(() => {
    try {
      const token = getAccessToken() || '';
      return !!token && isAdmin(token);
    } catch {
      return false;
    }
  }, []);

  // Get pipeline actions from module config (you'll need to pass this via props or context)
  const pipelineActions = props.request?.pipelineActions || [];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    props.onMenuOpen && props.onMenuOpen();
  };

  const handleClose = () => {
    setAnchorEl(null);
    props.onMenuClose && props.onMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleClose();
    props.onMenuClose && props.onMenuClose();
  };

  const handleInvalidateClick = () => {
    setInvalidateDialogOpen(true);
    handleClose();
    props.onMenuClose && props.onMenuClose();
  };

  const handleDeleteDialogClose = () => {
    console.log("handleDeleteDialogClose");
    setDeleteDialogOpen(false);
    props.onMenuClose && props.onMenuClose();
  };

  const handleInvalidateDialogClose = () => {
    setInvalidateDialogOpen(false);
    props.onMenuClose && props.onMenuClose();
  };

  const handleActionsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleActionClick = (action: any) => {
    setSelectedAction(action);
    setPipelineActionDialogOpen(true);
    handleActionsMenuClose();
    handleClose();
  };

  const handlePipelineActionDialogClose = () => {
    setPipelineActionDialogOpen(false);
    setSelectedAction(null);
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: ITEM_HEIGHT * 8,
            width: '20ch',
          },
        }}
      >
        <MenuList dense sx={{ paddingY: 0 }}>
          <MenuItem component={Link}
            to={`/module/${props.request.platform}/${props.request.name}/${props.request.pullRequestId}`}
            disabled={!props.request.isEditAllowed || props.request.statusType === "delete" || props.request.status === "cancelled"}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            Edit
          </MenuItem>
          <MenuItem component={Link}
            to={`/user/requests/${props.request.latestPullRequestId || props.request.pullRequestId}`}
            disabled={props.request.statusType === "delete" && props.request.prStatus === "completed"}
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            View
          </MenuItem>
          
          {pipelineActions.length > 0 && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem 
                onClick={handleActionsMenuOpen}
                disabled={props.request.status === "cancelled" || props.request.statusType === "delete"}
                sx={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon>
                    <BoltIcon fontSize="small" />
                  </ListItemIcon>
                  Actions
                </Box>
                <ChevronRightIcon fontSize="small" sx={{ ml: 2 }} />
              </MenuItem>
            </>
          )}
          
          <Divider sx={{ my: 0.5 }} />
          {isAdminUser && (
            <MenuItem onClick={handleInvalidateClick} disabled={props.request.statusType === "delete" || props.request.status === "cancelled"}>
              <ListItemIcon>
                <RestartAltIcon fontSize="small" />
              </ListItemIcon>
              Reset Request
            </MenuItem>
          )}
          {isAdminUser && <Divider sx={{ my: 0.5 }} />}
          <MenuItem onClick={handleDeleteClick} disabled={props.request.statusType === "delete" || props.request.status === "cancelled"}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            Delete
          </MenuItem>
        </MenuList>
      </Menu>
      
      {/* Actions Submenu */}
      <Menu
        anchorEl={actionsMenuAnchor}
        open={actionsMenuOpen}
        onClose={handleActionsMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuList dense sx={{ paddingY: 0 }}>
          {pipelineActions
            .filter((action: any) => action.enabled !== false)
            .map((action: any) => (
              <MenuItem 
                key={action.name}
                onClick={() => handleActionClick(action)}
              >
                <ListItemIcon>
                  <BoltIcon fontSize="small" />
                </ListItemIcon>
                {action.name}
              </MenuItem>
            ))}
        </MenuList>
      </Menu>

      <DeleteResourceDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        requestId={props.request.pullRequestId}
        module={props.request}
        mode="delete"
        disabled={props.request.statusType === "delete" || props.request.status === "cancelled"}
        onSuccess={props.onDeleteSuccess}
      />

      <DeleteResourceDialog
        open={invalidateDialogOpen}
        onClose={handleInvalidateDialogClose}
        requestId={props.request.pullRequestId}
        module={props.request}
        mode="invalidate"
        disabled={props.request.statusType === "delete" || props.request.status === "cancelled"}
        onSuccess={props.onDeleteSuccess}
      />
      
      {selectedAction && (
        <PipelineActionDialog
          open={pipelineActionDialogOpen}
          onClose={handlePipelineActionDialogClose}
          action={selectedAction}
          request={props.request}
        />
      )}
    </>
  );
}

export default ResourceContextMenu;